import {
  asStableCallback,
  useStableCallback,
} from "@bezoned/shared/hooks/use-stable-callback.js";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Toaster,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { BezonedMenuTabs } from "../../../components/ui/common/bezoned-menu-tabs.js";
import type { BezonedMenuItem } from "../../../components/ui/common/bezoned-menu-tabs.js";
import { useToast } from "../../../hooks/use-toast.js";
import {
  teamMemberList,
  teamOfficeGet,
  teamUpdate,
} from "../../../services/api.service.js";
import { preloadImage } from "../../../utils/image.js";
import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { useAuth } from "../../auth/use-auth.js";
import { useTeamMemberMutations } from "../tabs/members/hooks/use-team-member-mutations.js";
import { TeamMembersTab } from "../tabs/members/team-members-tab.js";
import type { Team } from "../tabs/members/types.js";
import { TeamGeneralTab } from "../tabs/team-general-tab.js";
import type { TeamGeneralFormValue } from "../tabs/team-general-tab.js";
import { TeamEditArchiveTab } from "./tabs/team-edit-archive-tab.js";
import { TeamEditThemeTab } from "./tabs/theme/team-edit-theme-tab.js";

const TEAM_EDIT_TABS = [
  { label: "General" },
  { label: "Members" },
  { label: "Theme" },
  { label: "Archive" },
] as const;

type TabLabel = (typeof TEAM_EDIT_TABS)[number]["label"];

export interface TeamEditDialogProps {
  team: Team | null;
  open: boolean;
  onClose: () => void;
  onTeamUpdated?: () => void;
  onTeamRenamed?: (updatedTeam: { id: string; name: string }) => void;
}

interface TeamEditFlowProps extends TeamEditDialogProps {
  isEnterprise: boolean;
  isStarter: boolean;
}

const TEAM_NOT_FOUND_MESSAGE =
  "The office was not found or you don't have permission to edit it.";

const isTabLabel = (value: string): value is TabLabel =>
  TEAM_EDIT_TABS.some((tab) => tab.label === value);

export const TeamEditFlow = ({
  team: teamProp,
  isEnterprise,
  isStarter,
  open,
  onClose,
  onTeamUpdated,
  onTeamRenamed,
}: TeamEditFlowProps) => {
  const { user } = useAuth();
  const { toasterId, dispatchFailureToast, dispatchSuccessToast } = useToast();
  const [selectedTab, setSelectedTab] = useState<TabLabel>(
    TEAM_EDIT_TABS[0].label
  );
  const queryClient = useQueryClient();
  const msTeamId = teamProp?.id ?? null;

  const { data: teamQueryData } = useQuery({
    enabled: open && msTeamId !== null,
    queryFn: () => {
      if (!msTeamId) {
        throw new Error("Team ID is required");
      }

      return teamOfficeGet({
        msTeamId,
      });
    },
    queryKey: ["teamOfficeGet", msTeamId],
  });

  const teamMembersQuery = useQuery({
    enabled: open && msTeamId !== null,
    queryFn: () => {
      if (!msTeamId) {
        throw new Error("Team ID is required");
      }

      return teamMemberList({ msTeamId });
    },
    queryKey: ["getTeamMembers", msTeamId],
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const team = teamProp;
  const teamName = teamQueryData?.team.name ?? team?.name;
  const logoUrl = teamQueryData?.logoUrl ?? null;
  const membersMutations = useTeamMemberMutations({
    dispatchFailureToast,
    dispatchSuccessToast,
    msTeamId: team?.id ?? null,
    onDeleteSuccess: () => {
      // no-op
    },
  });
  const { addMembers, deleteMember, isAdding, isDeleting } = membersMutations;
  const handleDeleteMember = useStableCallback(deleteMember);

  const handleSelectTab = useCallback((tab: string) => {
    if (!isTabLabel(tab)) {
      return;
    }

    setSelectedTab(tab);
  }, []);

  useEffect(() => {
    if (open) {
      // Always start editing on the General tab for a newly opened dialog.
      // eslint-disable-next-line react/set-state-in-effect
      setSelectedTab(TEAM_EDIT_TABS[0].label);
    }
  }, [open]);

  useEffect(() => {
    if (!(open && msTeamId)) {
      return;
    }

    preloadImage(logoUrl);
  }, [logoUrl, open, msTeamId]);

  useEffect(() => {
    if (!teamMembersQuery.error) {
      return;
    }

    recordErrorInSentry(teamMembersQuery.error);
  }, [teamMembersQuery.error]);

  const handleSaveGeneral = useCallback(
    async ({ name }: TeamGeneralFormValue) => {
      if (!msTeamId) {
        return;
      }

      try {
        await teamUpdate({
          msTeamId,
          name,
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["teamsGet"] }),
          queryClient.invalidateQueries({ queryKey: ["getTeam", msTeamId] }),
          queryClient.invalidateQueries({
            queryKey: ["teamOfficeGet", msTeamId],
          }),
        ]);

        onTeamRenamed?.({
          id: msTeamId,
          name,
        });
        onTeamUpdated?.();
        dispatchSuccessToast({ message: "Office renamed." });
      } catch (error) {
        recordErrorInSentry(
          error instanceof Error ? error : new Error(String(error))
        );
        dispatchFailureToast({
          message: "Failed to rename the office. Please try again.",
        });
        throw error;
      }
    },
    [
      dispatchFailureToast,
      dispatchSuccessToast,
      msTeamId,
      onTeamUpdated,
      onTeamRenamed,
      queryClient,
    ]
  );

  const onTeamThemeUpdated = useCallback(() => {
    if (!msTeamId) {
      return;
    }

    queryClient.refetchQueries({ queryKey: ["teamsGet"] });
    queryClient.refetchQueries({ queryKey: ["getTeam", msTeamId] });
    queryClient.refetchQueries({ queryKey: ["teamOfficeGet", msTeamId] });
    dispatchSuccessToast({ message: "Team was updated." });
    onTeamUpdated?.();
  }, [dispatchSuccessToast, msTeamId, onTeamUpdated, queryClient]);

  const onTeamLogoUpdateError = useCallback(
    (_error: unknown) => {
      dispatchFailureToast({
        message: "Failed to save office logo. Please try again.",
      });
    },
    [dispatchFailureToast]
  );

  const menuItems = useMemo(() => {
    if (!msTeamId) {
      return [];
    }

    const teamMembers = teamMembersQuery.data ?? [];
    const teamMembersErrorMessage = teamMembersQuery.isError
      ? "Failed to load office members. Please try again."
      : null;

    const tabs: Record<TabLabel, React.ReactNode> = {
      Archive: (
        <TeamEditArchiveTab
          dispatchFailureToast={dispatchFailureToast}
          dispatchSuccessToast={dispatchSuccessToast}
          key={`${msTeamId}-archive`}
          onTeamUpdated={asStableCallback(() => {
            onTeamUpdated?.();
            onClose();
          })}
          team={team}
        />
      ),
      General: (
        <TeamGeneralTab
          initialName={teamName ?? ""}
          key={`${msTeamId}-general-${teamName ?? ""}`}
          onSubmitName={handleSaveGeneral}
          submitLabel="Save"
          teamLogo={{
            msTeamId,
            onChange: onTeamThemeUpdated,
            onError: onTeamLogoUpdateError,
            teamQueryData,
          }}
        />
      ),
      Members: (
        <TeamMembersTab
          channelId={teamQueryData?.channels[0]?.msChannelId}
          currentUserId={user.id}
          errorMessage={teamMembersErrorMessage}
          isLoading={teamMembersQuery.isFetching}
          isSaving={isAdding || isDeleting}
          key={`${msTeamId}-members`}
          isEnterprise={isEnterprise}
          members={teamMembers}
          onAddUsers={asStableCallback((users) => {
            addMembers(users.map((member) => member.id));
          })}
          onRemoveUser={handleDeleteMember}
          teamId={msTeamId}
          tenantId={user.tenantId}
        />
      ),
      Theme: (
        <TeamEditThemeTab
          key={`${msTeamId}-theme`}
          msTeamId={msTeamId}
          onChange={onTeamThemeUpdated}
          onError={onTeamLogoUpdateError}
          teamQueryData={teamQueryData}
        />
      ),
    };

    return TEAM_EDIT_TABS.filter(
      (tab) => !(isStarter && tab.label === "Theme")
    ).map<BezonedMenuItem>((tab) => ({
      component: tabs[tab.label],
      label: tab.label,
    }));
  }, [
    team,
    dispatchFailureToast,
    dispatchSuccessToast,
    handleSaveGeneral,
    isStarter,
    onTeamLogoUpdateError,
    onTeamThemeUpdated,
    addMembers,
    handleDeleteMember,
    isAdding,
    isDeleting,
    isEnterprise,
    onClose,
    onTeamUpdated,
    teamMembersQuery.data,
    msTeamId,
    teamMembersQuery.isError,
    teamMembersQuery.isFetching,
    teamName,
    teamQueryData,
    user.id,
    user.tenantId,
  ]);
  const handleOpenChange298 = useStableCallback((_event, data) => {
    if (!data.open) {
      onClose();
    }
  });

  return (
    <>
      <Dialog onOpenChange={handleOpenChange298} open={open}>
        <DialogSurface className="relative sm:max-h-[90lvh] sm:w-[90lvw] md:min-w-[900px] lg:min-w-[1000px]">
          <Button
            appearance="subtle"
            aria-label="Close dialog"
            className="absolute top-4 right-4 z-10"
            icon={<Dismiss24Regular />}
            onClick={onClose}
            type="button"
          />
          <DialogBody className="flex flex-col sm:max-h-[90lvh]">
            <DialogTitle>
              {teamName ? `Editing office ${teamName}` : "Editing office"}
            </DialogTitle>

            <DialogContent className="flex flex-1 flex-col overflow-hidden lg:flex-row">
              {!team && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-md border bg-white p-6 text-center">
                  <p className="text-bzd-gray-700 text-sm">
                    {TEAM_NOT_FOUND_MESSAGE}
                  </p>
                  <Button appearance="primary" onClick={onClose} type="button">
                    Close
                  </Button>
                </div>
              )}

              {Boolean(team) && (
                <>
                  <div className="border-bzd-border-100 w-full border-b p-4 lg:h-full lg:w-[200px] lg:flex-shrink-0 lg:border-r lg:border-b-0">
                    <BezonedMenuTabs
                      currentTab={selectedTab}
                      items={menuItems}
                      selectTab={handleSelectTab}
                    />
                  </div>
                  <div className="flex flex-1 flex-col overflow-y-auto px-6 pb-6 md:max-h-[600px] md:min-h-[600px]">
                    {menuItems.find((item) => item.label === selectedTab)
                      ?.component ?? <div>This tab is not available</div>}
                  </div>
                </>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <Toaster toasterId={toasterId} />
    </>
  );
};
